import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, teamLeaderNavItems, workerNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  FileText, 
  Users, 
  CheckCircle2, 
  Clock, 
  Signature,
  PenLine,
  DollarSign,
  MapPin,
  Calendar,
  AlertCircle,
  ChevronRight,
  AlertTriangle,
  Shield,
  HardHat
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamLeaderAgreements() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signature, setSignature] = useState("");
  
  // SJA Acknowledgement checkboxes
  const [sjaAcknowledged, setSjaAcknowledged] = useState({
    readHazards: false,
    understandPPE: false,
    acceptTerms: false
  });
  
  // Individual hazard acknowledgements
  const [hazardAcknowledged, setHazardAcknowledged] = useState({});
  
  // TL Cede percentage (0-3%)
  const [cedePercentage, setCedePercentage] = useState(0);

  useEffect(() => {
    fetchAgreements();
  }, []);

  // Reset checkboxes when dialog opens
  useEffect(() => {
    if (dialogOpen && selectedAgreement) {
      setSjaAcknowledged({
        readHazards: false,
        understandPPE: false,
        acceptTerms: false
      });
      setCedePercentage(0);
      setSignature("");
      
      // Reset hazard acknowledgements
      const hazards = selectedAgreement?.sja_form?.hazards || [];
      const initialHazardAck = {};
      hazards.forEach((_, idx) => {
        initialHazardAck[idx] = false;
      });
      setHazardAcknowledged(initialHazardAck);
    }
  }, [dialogOpen, selectedAgreement]);

  const fetchAgreements = async () => {
    try {
      const response = await axios.get(`${API}/team-agreements`);
      const data = response.data;
      if (Array.isArray(data)) {
        setAgreements(data);
      } else if (data?.agreements) {
        setAgreements(data.agreements);
      } else {
        setAgreements([]);
      }
    } catch (error) {
      console.error("Error fetching agreements:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (agreementId) => {
    try {
      await axios.put(`${API}/team-agreements/${agreementId}/approve`);
      toast.success("Allocation approved!");
      fetchAgreements();
      if (selectedAgreement?.agreement_id === agreementId) {
        const updated = await axios.get(`${API}/team-agreements/${agreementId}`);
        setSelectedAgreement(updated.data.agreement || updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const handleSign = async (agreementId) => {
    // Validate SJA acknowledgements
    if (!sjaAcknowledged.readHazards || !sjaAcknowledged.understandPPE || !sjaAcknowledged.acceptTerms) {
      toast.error("You must acknowledge all SJA requirements before signing");
      return;
    }
    
    if (!signature.trim()) {
      toast.error("Please enter your signature");
      return;
    }
    
    setSigning(true);
    try {
      // Send signature with optional cede percentage
      await axios.post(`${API}/team-agreements/${agreementId}/sign`, {
        signature: signature,
        cede_percentage: cedePercentage
      });
      toast.success("Agreement signed successfully!");
      setSignature("");
      setSjaAcknowledged({ readHazards: false, understandPPE: false, acceptTerms: false });
      setCedePercentage(0);
      fetchAgreements();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign");
    } finally {
      setSigning(false);
    }
  };

  const openAgreement = async (agreement) => {
    try {
      const response = await axios.get(`${API}/team-agreements/${agreement.agreement_id}`);
      const agreementData = response.data.agreement || response.data;
      setSelectedAgreement(agreementData);
      setDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load agreement details");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft": return "bg-gray-500/20 text-gray-400";
      case "pending_approval": return "bg-yellow-500/20 text-yellow-400";
      case "pending_signatures": return "bg-blue-500/20 text-blue-400";
      case "all_approved": return "bg-blue-500/20 text-blue-400";
      case "signed": return "bg-green-500/20 text-green-400";
      case "completed": return "bg-green-500/20 text-green-400";
      case "active": return "bg-purple-500/20 text-purple-400";
      default: return "bg-gray-500/20 text-gray-400";
    }
  };

  const getMyAllocation = (agreement) => {
    return agreement.members?.find(m => m.user_id === user?.user_id);
  };

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return "kr 0";
    return new Intl.NumberFormat('nb-NO', { 
      style: 'currency', 
      currency: 'NOK',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Check if all SJA requirements are acknowledged
  const allSjaAcknowledged = sjaAcknowledged.readHazards && sjaAcknowledged.understandPPE && sjaAcknowledged.acceptTerms;

  // Determine nav items based on user role
  const navItems = user?.role === "worker" ? workerNavItems : teamLeaderNavItems;

  return (
    <DashboardLayout navItems={navItems} title="Team Agreements">
      <div className="space-y-6" data-testid="team-agreements-page">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#23F7DD]/10 rounded-lg">
                  <FileText className="w-5 h-5 text-[#23F7DD]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#23F7DD]">{agreements.length}</p>
                  <p className="text-xs text-[#94A3B8]">Total Agreements</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/10 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">
                    {agreements.filter(a => a.status === "pending_approval" || a.status === "pending_signatures").length}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Pending Action</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <PenLine className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-400">
                    {agreements.filter(a => a.status === "all_approved").length}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Ready to Sign</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">
                    {agreements.filter(a => a.status === "signed" || a.status === "completed").length}
                  </p>
                  <p className="text-xs text-[#94A3B8]">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agreements List */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">My Agreements</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : agreements.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">No agreements yet</p>
                <p className="text-sm text-[#475569] mt-2">
                  Agreements are created when clients select your team for projects
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {agreements.map((agreement) => {
                  const myAllocation = getMyAllocation(agreement);
                  const signedCount = agreement.members?.filter(m => m.signed).length || 0;
                  const totalMembers = agreement.members?.length || 0;
                  const needsMySignature = myAllocation && !myAllocation.signed;
                  
                  return (
                    <div
                      key={agreement.agreement_id}
                      className={`p-4 rounded-lg border transition-colors ${
                        needsMySignature 
                          ? 'bg-orange-500/10 border-orange-500/30 hover:border-orange-500' 
                          : 'bg-white/5 border-white/10 hover:border-[#23F7DD]/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 cursor-pointer" onClick={() => openAgreement(agreement)}>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-medium">{agreement.project_title}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(agreement.status)}`}>
                              {agreement.status?.replace(/_/g, ' ').toUpperCase()}
                            </span>
                            {needsMySignature && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-400 animate-pulse">
                                ACTION REQUIRED
                              </span>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {formatCurrency(agreement.total_amount)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {totalMembers} members
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {agreement.location}
                            </span>
                          </div>
                          
                          {/* Progress bar */}
                          <div className="mt-4">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[#94A3B8] w-20">Signatures:</span>
                              <Progress value={(signedCount / totalMembers) * 100} className="h-2 flex-1" />
                              <span className="text-xs text-[#94A3B8]">{signedCount}/{totalMembers}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right flex flex-col items-end gap-3">
                          {myAllocation && (
                            <div>
                              <p className="text-sm text-[#94A3B8]">Your share:</p>
                              <p className="text-xl font-bold text-[#23F7DD]">
                                {myAllocation.current_percentage}%
                              </p>
                              <p className="text-sm text-white">
                                {formatCurrency(myAllocation.amount)}
                              </p>
                            </div>
                          )}
                          
                          {/* Sign Here Button */}
                          {needsMySignature ? (
                            <Button
                              onClick={() => openAgreement(agreement)}
                              className="bg-orange-500 text-white hover:bg-orange-600 font-bold animate-pulse"
                              data-testid="sign-here-btn"
                            >
                              <Signature className="w-4 h-4 mr-2" />
                              Sign Here
                            </Button>
                          ) : myAllocation?.signed ? (
                            <span className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4" />
                              Signed
                            </span>
                          ) : (
                            <Button
                              variant="outline"
                              onClick={() => openAgreement(agreement)}
                              className="border-white/20 text-white hover:bg-white/10"
                            >
                              View Details
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agreement Details Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-xl">Team Agreement Contract</DialogTitle>
            </DialogHeader>
            
            {selectedAgreement && (
              <div className="space-y-6">
                {/* Contract Header */}
                <div className="p-4 bg-gradient-to-r from-[#23F7DD]/10 to-purple-500/10 rounded-lg border border-[#23F7DD]/30">
                  <h3 className="text-xl font-bold text-white mb-4">{selectedAgreement.project_title}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-[#64748B]">Total Budget</span>
                      <p className="text-[#23F7DD] font-bold text-lg">{formatCurrency(selectedAgreement.total_amount)}</p>
                    </div>
                    <div>
                      <span className="text-[#64748B]">Location</span>
                      <p className="text-white">{selectedAgreement.location || "TBD"}</p>
                    </div>
                    <div>
                      <span className="text-[#64748B]">Start Date</span>
                      <p className="text-white">{selectedAgreement.start_date || "TBD"}</p>
                    </div>
                    <div>
                      <span className="text-[#64748B]">Status</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedAgreement.status)}`}>
                        {selectedAgreement.status?.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Project Details Section */}
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#23F7DD]" />
                    Project Details - What You Are Approving
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {selectedAgreement.scope_of_work && (
                      <div className="col-span-2">
                        <span className="text-[#64748B]">Scope of Work:</span>
                        <p className="text-white mt-1 whitespace-pre-wrap">{selectedAgreement.scope_of_work}</p>
                      </div>
                    )}
                    {selectedAgreement.project_type && (
                      <div>
                        <span className="text-[#64748B]">Project Type:</span>
                        <p className="text-white capitalize">{selectedAgreement.project_type}</p>
                      </div>
                    )}
                    {selectedAgreement.complexity && (
                      <div>
                        <span className="text-[#64748B]">Complexity:</span>
                        <p className="text-white capitalize">{selectedAgreement.complexity}</p>
                      </div>
                    )}
                    {selectedAgreement.deadline && (
                      <div>
                        <span className="text-[#64748B]">Deadline:</span>
                        <p className="text-white">{selectedAgreement.deadline}</p>
                      </div>
                    )}
                    {selectedAgreement.estimated_duration && (
                      <div>
                        <span className="text-[#64748B]">Estimated Duration:</span>
                        <p className="text-white">{selectedAgreement.estimated_duration} hours</p>
                      </div>
                    )}
                    {selectedAgreement.required_crew_size && (
                      <div>
                        <span className="text-[#64748B]">Required Crew:</span>
                        <p className="text-white">{selectedAgreement.required_crew_size} workers</p>
                      </div>
                    )}
                    {selectedAgreement.safety_requirements && (
                      <div className="col-span-2">
                        <span className="text-[#64748B]">Safety Requirements:</span>
                        <p className="text-white mt-1">{selectedAgreement.safety_requirements}</p>
                      </div>
                    )}
                    {selectedAgreement.payment_terms && (
                      <div className="col-span-2">
                        <span className="text-[#64748B]">Payment Terms:</span>
                        <p className="text-white mt-1">{selectedAgreement.payment_terms}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Team Allocations */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#23F7DD]" />
                    Team Allocations
                  </h4>
                  
                  <div className="space-y-3">
                    {selectedAgreement.members?.map((member) => {
                      const isMe = member.user_id === user?.user_id;
                      
                      return (
                        <div 
                          key={member.user_id}
                          className={`p-3 rounded-lg border ${isMe ? 'bg-[#23F7DD]/10 border-[#23F7DD]/30' : 'bg-black/30 border-white/10'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                member.role === 'team_leader' ? 'bg-purple-500/20' : 'bg-[#23F7DD]/20'
                              }`}>
                                <span className={`text-sm font-bold ${
                                  member.role === 'team_leader' ? 'text-purple-400' : 'text-[#23F7DD]'
                                }`}>
                                  {member.role === 'team_leader' ? 'TL' : 'W'}
                                </span>
                              </div>
                              <div>
                                <p className="text-white font-medium">
                                  {member.user_name || member.name} {isMe && <span className="text-[#23F7DD]">(You)</span>}
                                </p>
                                <p className="text-xs text-[#94A3B8]">{member.worker_code}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-white font-bold">{member.current_percentage}%</p>
                                <p className="text-sm text-[#94A3B8]">{formatCurrency(member.amount)}</p>
                              </div>
                              
                              {member.signed ? (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Signed
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* SJA Form - Required Reading */}
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    Safe Job Analysis (SJA) - MUST READ & ACKNOWLEDGE
                  </h4>
                  
                  {selectedAgreement.sja_form ? (
                    <div className="space-y-4">
                      {/* Job Description */}
                      {selectedAgreement.sja_form.job_description && (
                        <div className="p-3 bg-black/30 rounded-lg">
                          <p className="text-red-400 font-medium text-sm mb-1">Job Description:</p>
                          <p className="text-white">{selectedAgreement.sja_form.job_description}</p>
                        </div>
                      )}
                      
                      {/* Hazards with Checkboxes */}
                      {selectedAgreement.sja_form.hazards && Array.isArray(selectedAgreement.sja_form.hazards) && selectedAgreement.sja_form.hazards.filter(h => h && (h.hazard || h.description)).length > 0 && (
                        <div className="p-3 bg-black/30 rounded-lg">
                          <p className="text-red-400 font-medium text-sm mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Identified Hazards - Check each to confirm you understand:
                          </p>
                          <div className="space-y-3">
                            {selectedAgreement.sja_form.hazards.filter(h => h && (h.hazard || h.description)).map((h, idx) => (
                              <div 
                                key={idx} 
                                className={`p-3 rounded-lg border transition-all ${
                                  hazardAcknowledged[idx] 
                                    ? 'bg-green-500/10 border-green-500/30' 
                                    : 'bg-red-500/5 border-red-500/20'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <Checkbox
                                    id={`hazard-${idx}`}
                                    checked={hazardAcknowledged[idx] || false}
                                    onCheckedChange={(checked) => setHazardAcknowledged(prev => ({ ...prev, [idx]: checked }))}
                                    className="mt-1 border-red-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                                  />
                                  <Label htmlFor={`hazard-${idx}`} className="cursor-pointer flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <AlertTriangle className={`w-4 h-4 ${hazardAcknowledged[idx] ? 'text-green-400' : 'text-yellow-400'}`} />
                                      <span className="text-white font-medium">{h.hazard || h.description || h}</span>
                                      {h.risk_level && (
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                          h.risk_level === 'red' ? 'bg-red-500/20 text-red-400' :
                                          h.risk_level === 'yellow' ? 'bg-yellow-500/20 text-yellow-400' :
                                          'bg-green-500/20 text-green-400'
                                        }`}>{h.risk_level === 'red' ? 'HIGH RISK' : h.risk_level === 'yellow' ? 'MEDIUM RISK' : 'LOW RISK'}</span>
                                      )}
                                    </div>
                                    {(h.mitigation || h.preventive_measures) && (
                                      <p className="text-sm text-[#94A3B8] ml-6">
                                        <span className="text-green-400">✓ Preventive measure:</span> {h.mitigation || h.preventive_measures}
                                      </p>
                                    )}
                                  </Label>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Hazards acknowledgement status */}
                          <div className="mt-3 flex items-center justify-between text-sm">
                            <span className="text-[#64748B]">
                              Acknowledged: {Object.values(hazardAcknowledged).filter(Boolean).length} / {selectedAgreement.sja_form.hazards.filter(h => h && (h.hazard || h.description)).length}
                            </span>
                            {Object.values(hazardAcknowledged).every(Boolean) && selectedAgreement.sja_form.hazards.filter(h => h && (h.hazard || h.description)).length > 0 && (
                              <span className="text-green-400 flex items-center gap-1">
                                <CheckCircle2 className="w-4 h-4" />
                                All hazards acknowledged
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* PPE Required */}
                      {selectedAgreement.sja_form.ppe_required && (
                        <div className="p-3 bg-black/30 rounded-lg">
                          <p className="text-red-400 font-medium text-sm mb-2 flex items-center gap-2">
                            <HardHat className="w-4 h-4" />
                            Required PPE (Personal Protective Equipment):
                          </p>
                          {Array.isArray(selectedAgreement.sja_form.ppe_required) ? (
                            <div className="flex flex-wrap gap-2">
                              {selectedAgreement.sja_form.ppe_required.map((ppe, idx) => (
                                <span key={idx} className="px-3 py-1.5 bg-red-500/20 text-red-300 rounded-full text-sm font-medium capitalize">
                                  {ppe.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-white">{selectedAgreement.sja_form.ppe_required}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Emergency Contact */}
                      {selectedAgreement.sja_form.emergency_contact && (
                        <div className="p-3 bg-black/30 rounded-lg">
                          <p className="text-red-400 font-medium text-sm mb-1">Emergency Contact:</p>
                          <p className="text-white">{selectedAgreement.sja_form.emergency_contact}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-[#94A3B8]">Standard safety protocols apply. Contact project manager for specific requirements.</p>
                  )}
                </div>

                {/* Waiting Time Terms */}
                {selectedAgreement.waiting_time_terms && selectedAgreement.waiting_time_terms.enabled !== false && (
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-400" />
                      Waiting Time Compensation
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      {selectedAgreement.waiting_time_terms.max_hours_per_person && (
                        <div>
                          <span className="text-[#64748B]">Max Hours/Person:</span>
                          <p className="text-white font-medium">{selectedAgreement.waiting_time_terms.max_hours_per_person} hours</p>
                        </div>
                      )}
                      {(selectedAgreement.waiting_time_terms.hourly_rate > 0 || selectedAgreement.waiting_time_terms.rate_per_hour > 0) && (
                        <div>
                          <span className="text-[#64748B]">Hourly Rate:</span>
                          <p className="text-white font-medium">kr {selectedAgreement.waiting_time_terms.hourly_rate || selectedAgreement.waiting_time_terms.rate_per_hour}</p>
                        </div>
                      )}
                    </div>
                    {/* Explanation message */}
                    <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 mb-3">
                      <p className="text-blue-300 text-sm leading-relaxed">
                        <strong>Important:</strong> The waiting time compensation is <strong>already included</strong> in the total project price. 
                        If the project is completed earlier than expected, this amount will <strong>not be deducted</strong> from your payment.
                      </p>
                    </div>
                  </div>
                )}

                {/* Signing Section - Only for unsigned members */}
                {(() => {
                  const myAllocation = getMyAllocation(selectedAgreement);
                  if (!myAllocation) return null;
                  
                  if (myAllocation.signed) {
                    return (
                      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <div className="flex items-center gap-3 text-green-400">
                          <CheckCircle2 className="w-6 h-6" />
                          <div>
                            <p className="font-semibold">You have signed this agreement</p>
                            <p className="text-sm text-green-400/70">Signed on {myAllocation.signed_at ? new Date(myAllocation.signed_at).toLocaleString('nb-NO') : 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <>
                      {/* Cede Percentage to TL (Optional) - SEPARATE SECTION */}
                      {(() => {
                        // Check if current user is NOT the team leader in this agreement
                        const isTeamLeaderInAgreement = selectedAgreement.team_leader_id === user?.user_id;
                        const hasMultipleMembers = selectedAgreement.members?.length > 1;
                        
                        if (!isTeamLeaderInAgreement && hasMultipleMembers) {
                          return (
                            <div className="p-4 bg-gradient-to-r from-[#23F7DD]/10 to-green-500/10 border border-[#23F7DD]/30 rounded-lg mb-4">
                              <div className="flex items-center gap-2 mb-4">
                                <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                                <h4 className="text-white font-semibold">Team Leader Coordination Fee (Optional)</h4>
                              </div>
                              <div className="flex items-center justify-between mb-3">
                                <Label className="text-[#94A3B8]">Cede percentage to Team Leader</Label>
                                <span className="text-[#23F7DD] font-bold text-2xl">{cedePercentage}%</span>
                              </div>
                              <Slider
                                value={[cedePercentage]}
                                onValueChange={(value) => setCedePercentage(value[0])}
                                max={3}
                                step={0.5}
                                className="mb-3"
                              />
                              <div className="flex justify-between text-xs text-[#64748B]">
                                <span>0%</span>
                                <span>1%</span>
                                <span>2%</span>
                                <span>3%</span>
                              </div>
                              <p className="text-sm text-[#94A3B8] mt-3">
                                You can optionally cede up to 3% of your share to the Team Leader as a coordination fee.
                              </p>
                              {cedePercentage > 0 && (
                                <div className="mt-3 p-2 bg-[#23F7DD]/20 rounded text-center">
                                  <span className="text-[#23F7DD] font-medium">
                                    Amount to cede: {formatCurrency((myAllocation.amount || 0) * cedePercentage / 100)}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Sign Agreement Section */}
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-6">
                        <h4 className="text-white font-semibold flex items-center gap-2">
                          <Signature className="w-5 h-5 text-purple-400" />
                          Sign Agreement
                        </h4>
                        
                        {/* SJA Acknowledgement Checkboxes */}
                        <div className="space-y-4">
                          <p className="text-sm text-[#94A3B8]">Before signing, you must acknowledge that you have read and understood the safety requirements:</p>
                          
                          <div className="space-y-3">
                            <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                              <Checkbox
                                id="readHazards"
                                checked={sjaAcknowledged.readHazards}
                                onCheckedChange={(checked) => setSjaAcknowledged(prev => ({ ...prev, readHazards: checked }))}
                                className="mt-0.5 border-red-400 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                              />
                              <Label htmlFor="readHazards" className="text-white cursor-pointer text-sm leading-relaxed">
                                I have read and understood all <span className="text-red-400 font-semibold">identified hazards</span> for this project and the preventive measures required.
                              </Label>
                            </div>
                            
                            <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                              <Checkbox
                                id="understandPPE"
                                checked={sjaAcknowledged.understandPPE}
                                onCheckedChange={(checked) => setSjaAcknowledged(prev => ({ ...prev, understandPPE: checked }))}
                                className="mt-0.5 border-red-400 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                              />
                              <Label htmlFor="understandPPE" className="text-white cursor-pointer text-sm leading-relaxed">
                                I understand and will comply with all <span className="text-red-400 font-semibold">PPE requirements</span> specified in the Safe Job Analysis.
                              </Label>
                            </div>
                            
                            <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                              <Checkbox
                                id="acceptTerms"
                                checked={sjaAcknowledged.acceptTerms}
                                onCheckedChange={(checked) => setSjaAcknowledged(prev => ({ ...prev, acceptTerms: checked }))}
                                className="mt-0.5 border-green-400 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                              />
                              <Label htmlFor="acceptTerms" className="text-white cursor-pointer text-sm leading-relaxed">
                                I accept the <span className="text-green-400 font-semibold">contract terms</span>, including my allocation of <span className="text-[#23F7DD] font-bold">{myAllocation.current_percentage}%</span> ({formatCurrency(myAllocation.amount)}).
                              </Label>
                            </div>
                          </div>
                        </div>
                        
                        {/* Signature Input */}
                        <div className="space-y-2">
                          <Label className="text-white font-medium">Type your full name to sign</Label>
                          <Input
                            value={signature}
                            onChange={(e) => setSignature(e.target.value)}
                            placeholder="Enter your full legal name"
                            className="bg-black/50 border-white/10 text-white focus:border-purple-400 text-lg"
                            disabled={!allSjaAcknowledged}
                          />
                          {!allSjaAcknowledged && (
                            <p className="text-xs text-yellow-400">Please acknowledge all checkboxes above before signing</p>
                          )}
                        </div>
                        
                        {/* Sign Button */}
                        <Button
                          onClick={() => handleSign(selectedAgreement.agreement_id)}
                          disabled={signing || !signature.trim() || !allSjaAcknowledged}
                          className="w-full bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed h-12 text-lg font-semibold"
                        >
                          {signing ? (
                            <span className="flex items-center gap-2">
                              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              Signing Agreement...
                            </span>
                          ) : (
                            <>
                              <Signature className="w-5 h-5 mr-2" />
                              Sign Agreement
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
